import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import { TituloDePantalla } from '@/shared/components/TituloDePantalla'
import { comoEventoDeCambio } from '@/shared/lib/opcionesDeSelect'
import SelectBuscable from '@/shared/components/SelectBuscable'
import { useState, useEffect } from 'react'
import { Checkbox } from '@/shared/components/Checkbox'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Save, X, User, Mail, Eye, EyeOff, RefreshCw, Check, Shield } from 'lucide-react'
import { apiService, type ApiError } from '@/services/api'
import { getAreasCatalog, type Area } from '@/features/configuracion/api/areaService'
import type { Role } from '@/types/rbac.types'
import { hasRole, userHasPermission } from '@/shared/lib/roleUtils'
import { useAuth } from '@/shared/hooks/useAuth'

const CreateUser = () => {
  const navigate = useNavigate()
  const { user } = useAuth();
  const esSuperadmin = hasRole(user, 'MASTER')
  const [isCreating, setIsCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState(false)
  const [availableRoles, setAvailableRoles] = useState<Role[]>([])
  const [selectedRoleKey, setSelectedRoleKey] = useState<string>('')
  const [isLoadingRoles, setIsLoadingRoles] = useState(true)

  // Estados para áreas
  const [areas, setAreas] = useState<Area[]>([])
  const [isLoadingAreas, setIsLoadingAreas] = useState(false)

  // Form data
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    areaId: null as number | null,
    isAreaManager: false,
    canAssignSalaries: false,
    canBypassSupplierDocumentValidation: false,
    receivesToolDecommissionEmail: false,
    receivesSupplierApprovalEmail: false
  })

  // Load available roles on component mount
  useEffect(() => {
    loadAvailableRoles()
    loadAreas()
  }, [])

  const loadAreas = async () => {
    setIsLoadingAreas(true)
    try {
      const areasData = await getAreasCatalog()
      const activeAreas = areasData.filter(area => area.activa)
      setAreas(activeAreas)
    } catch (err) {
      console.error('Error al cargar áreas:', err)
    } finally {
      setIsLoadingAreas(false)
    }
  }

  const loadAvailableRoles = async () => {
    setIsLoadingRoles(true)
    try {
      const roles = await apiService.getRolesCatalog()
      setAvailableRoles(roles)
    } catch (err) {
      const apiError = err as ApiError
      console.error('Error loading available roles:', apiError)
      setError('Error al cargar los roles disponibles')
    } finally {
      setIsLoadingRoles(false)
    }
  }

  // Password strength evaluation
  const evaluatePasswordStrength = (password: string) => {
    let score = 0
    const checks = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      numbers: /\d/.test(password),
      symbols: /[^A-Za-z0-9]/.test(password),
      noCommon: !['password', '123456', 'qwerty', 'admin'].some(common =>
        password.toLowerCase().includes(common)
      )
    }

    // Calculate score
    if (checks.length) score += 20
    if (checks.uppercase) score += 15
    if (checks.lowercase) score += 15
    if (checks.numbers) score += 15
    if (checks.symbols) score += 20
    if (checks.noCommon) score += 15

    // Determine strength level
    let strength = 'débil'
    let color = 'red'
    if (score >= 80) {
      strength = 'fuerte'
      color = 'green'
    } else if (score >= 60) {
      strength = 'media'
      color = 'yellow'
    }

    return { score, checks, strength, color }
  }

  // Generate strong password
  const generateStrongPassword = () => {
    const lowercase = 'abcdefghijklmnopqrstuvwxyz'
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
    const numbers = '0123456789'
    const symbols = '!@#$%^&*()_+-=[]{}|;:,.<>?'

    let password = ''

    // Ensure at least one character from each category
    password += lowercase[Math.floor(Math.random() * lowercase.length)]
    password += uppercase[Math.floor(Math.random() * uppercase.length)]
    password += numbers[Math.floor(Math.random() * numbers.length)]
    password += symbols[Math.floor(Math.random() * symbols.length)]

    // Fill the rest with random characters
    const allChars = lowercase + uppercase + numbers + symbols
    for (let i = 4; i < 12; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)]
    }

    // Shuffle the password
    return password.split('').sort(() => Math.random() - 0.5).join('')
  }

  const passwordStrength = evaluatePasswordStrength(formData.password)

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target


    const finalValue = type === 'checkbox'
      ? (e.target as HTMLInputElement).checked
      : value

    setFormData(prev => ({
      ...prev,
      [name]: name === 'areaId'
        ? (value === '' ? null : parseInt(value))
        : finalValue
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Basic validation
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.password) {
      setError('Todos los campos son obligatorios')
      return
    }

    // Role validation
    if (!selectedRoleKey) {
      setError('Debes seleccionar un rol para el usuario')
      return
    }

    // Password strength validation
    const passwordCheck = evaluatePasswordStrength(formData.password)
    if (passwordCheck.score < 60) {
      setError('La contraseña es demasiado débil. Por favor usa una contraseña más fuerte o haz clic en "Generar" para una segura.')
      return
    }

    setIsCreating(true)
    setError(null)

    try {
      // Step 1: Create the user
      const newUser = await apiService.createUser(formData)

      // Step 2: Assign the role to the newly created user
      try {
        await apiService.assignRolesToUser(newUser.id, [selectedRoleKey])
      } catch (roleError) {
        const apiError = roleError as ApiError
        console.error('Error assigning role to user:', apiError)
        // User was created but role assignment failed
        setError(`Usuario creado pero no se pudo asignar el rol: ${Array.isArray(apiError.message) ? apiError.message.join(', ') : apiError.message}`)
        // Don't navigate back yet, let user see the error
        return
      }

      // Success - both user creation and role assignment worked
      navigate('/users')
    } catch (err) {
      const apiError = err as ApiError
      setError(Array.isArray(apiError.message) ? apiError.message.join(', ') : apiError.message || 'Error al crear el usuario')
      console.error('Error creating user:', apiError)
    } finally {
      setIsCreating(false)
    }
  }

  const handleCancel = () => {
    navigate(-1 as any)
  }

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  const handleGeneratePassword = () => {
    const newPassword = generateStrongPassword()
    setFormData(prev => ({
      ...prev,
      password: newPassword
    }))
    setShowPassword(true) // Show the generated password
  }

  return (
    <div className="space-y-6 my-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => handleCancel()}
            className={estiloDeBoton('secundario', 'mb-6')}
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </button>
          <div>
            <TituloDePantalla nivel="interna">Crear Nuevo Usuario</TituloDePantalla>
            <p className="text-gray-600">Agregar un nuevo usuario al sistema</p>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-2">
          <X className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Create User Form */}
      <form onSubmit={handleSubmit} autoComplete="off" className="space-y-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <User className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Información del Usuario</h2>
                <p className="text-sm text-gray-600">Ingresa los detalles del nuevo usuario</p>
              </div>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* First Name */}
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre *
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ingresa el nombre"
                />
              </div>

              {/* Last Name */}
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                  Apellido *
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ingresa el apellido"
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  <Mail className="w-4 h-4 inline mr-1" />
                  Dirección de Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  autoComplete="off"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ingresa la dirección de email"
                />
              </div>

              {/* Role Selection */}
              <div>
                <label htmlFor="roleKey" className="block text-sm font-medium text-gray-700 mb-2">
                  <Shield className="w-4 h-4 inline mr-1" />
                  Rol de Usuario *
                </label>
                {isLoadingRoles ? (
                  <div className="flex items-center justify-center py-2 px-3 border border-gray-300 rounded-lg bg-gray-50">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent mr-2"></div>
                    <span className="text-sm text-gray-600">Cargando roles...</span>
                  </div>
                ) : availableRoles.length === 0 ? (
                  <div className="py-2 px-3 border border-red-300 rounded-lg bg-red-50 text-sm text-red-700">
                    No hay roles disponibles
                  </div>
                ) : (
                  <SelectBuscable
                    variante="formulario"
                    id="roleKey"
                    name="roleKey"
                    value={selectedRoleKey}
                    onChange={setSelectedRoleKey}
                    required
                    placeholder="Selecciona un rol"
                    placeholderDeBusqueda="Buscar rol..."
                    opciones={availableRoles.map((role) => ({ value: role.key, label: role.name }))}
                  />
                )}
                <p className="text-xs text-gray-500 mt-1">
                  El rol determina los permisos y accesos del usuario en el sistema
                </p>
              </div>

              {/* Área asignada */}
              <div>
                <label htmlFor="areaId" className="block text-sm font-medium text-gray-700 mb-2">
                  Departamento/Área
                </label>
                {isLoadingAreas ? (
                  <div className="flex items-center justify-center py-2 px-3 border border-gray-300 rounded-lg bg-gray-50">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent mr-2"></div>
                    <span className="text-sm text-gray-600">Cargando áreas...</span>
                  </div>
                ) : (
                  <SelectBuscable
                    variante="formulario"
                    id="areaId"
                    name="areaId"
                    value={String(formData.areaId || '')}
                    onChange={(valor) => handleInputChange(comoEventoDeCambio('areaId', valor))}
                    placeholder="Seleccionar"
                    placeholderDeBusqueda="Buscar área..."
                    opciones={areas.map((area) => ({ value: String(area.id), label: area.nombre }))}
                  />
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Opcional: asigna el usuario a un departamento o área específica
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Permissions ExtraOrdinaries Section - Separate Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h2 className="text-lg font-semibold text-gray-900">Permisos extraordinarios</h2>
          </div>

          <div className="p-6">
            {/* Usamos flex-col para forzar una sola columna vertical */}
            <div className="flex flex-col gap-6">

              {/* Checkbox 1: Gerente de departamento/área */}
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <Checkbox
                    id="isAreaManager"
                    name="isAreaManager"
                    checked={formData.isAreaManager}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label
                    htmlFor="isAreaManager"
                    className="font-medium text-gray-700 cursor-pointer select-none"
                  >
                    Gerente de departamento/área
                  </label>
                  <p className="text-gray-500 text-xs mt-0.5">
                    Permite al usuario realizar la preaprobación de solicitudes
                    de personal de su área, rechazarlas y otorgar la
                    aprobación definitiva una vez que el salario ha sido
                    asignado. Este rol participa como filtro final antes de
                    que la vacante quede formalmente autorizada.
                  </p>
                </div>
              </div>

              {/* Checkbox 2: Asignar Sueldos */}
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <Checkbox
                    id="canAssignSalaries"
                    name="canAssignSalaries"
                    checked={formData.canAssignSalaries}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label
                    htmlFor="canAssignSalaries"
                    className="font-medium text-gray-700 cursor-pointer select-none"
                  >
                    Asignar sueldos
                  </label>
                  <p className="text-gray-500 text-xs mt-0.5">
                    Permite al usuario definir y registrar el salario propuesto
                    para una solicitud de personal, como paso previo a la
                    aprobación definitiva. Sin esta acción, la solicitud no
                    puede concluir su proceso de autorización.
                  </p>
                </div>
              </div>

              {/* Checkbox 3: Bypass de expediente de proveedor (SP-212/213) */}
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <Checkbox
                    id="canBypassSupplierDocumentValidation"
                    name="canBypassSupplierDocumentValidation"
                    checked={formData.canBypassSupplierDocumentValidation}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label
                    htmlFor="canBypassSupplierDocumentValidation"
                    className="font-medium text-gray-700 cursor-pointer select-none"
                  >
                    Autorizar bypass proveedor
                  </label>
                  <p className="text-gray-500 text-xs mt-0.5">
                    Permite al usuario autorizar que un proveedor opere aunque su
                    expediente esté incompleto. Cada excepción queda registrada
                    con su usuario, la fecha y el motivo.
                  </p>
                </div>
              </div>

              {/* Checkbox 4: Aviso de baja de herramienta (CR-02) */}
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <Checkbox
                    id="receivesToolDecommissionEmail"
                    name="receivesToolDecommissionEmail"
                    checked={formData.receivesToolDecommissionEmail}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label
                    htmlFor="receivesToolDecommissionEmail"
                    className="font-medium text-gray-700 cursor-pointer select-none"
                  >
                    Recibir aviso de baja de herramienta
                  </label>
                  <p className="text-gray-500 text-xs mt-0.5">
                    Le llega un correo cada vez que se da de baja una herramienta,
                    con sus datos y el motivo. El aviso también le llega a quien
                    tenía la herramienta a su cargo. No habilita dar de baja.
                  </p>
                </div>
              </div>

              {/* Checkbox 5: Aviso de alta de proveedor a Tesorería (CC-04, SP-254).
                  Solo lo ve el superadministrador; el back acepta la bandera de
                  quien ya puede editar usuarios. */}
              {esSuperadmin && (
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <Checkbox
                      id="receivesSupplierApprovalEmail"
                      name="receivesSupplierApprovalEmail"
                      checked={formData.receivesSupplierApprovalEmail}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label
                      htmlFor="receivesSupplierApprovalEmail"
                      className="font-medium text-gray-700 cursor-pointer select-none"
                    >
                      Recibe aviso de alta de proveedor (Tesorería)
                    </label>
                    <p className="text-gray-500 text-xs mt-0.5">
                      Le llega un correo cada vez que un proveedor queda
                      aprobado, con los datos del proveedor y de su cuenta
                      bancaria. Solo le llega si el usuario está activo.
                    </p>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>

        {/* Password Section - Separate Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h2 className="text-lg font-semibold text-gray-900">Contraseña</h2>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Password Input Column */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Contraseña
                </label>

                {/* Password Input with Generator Button */}
                <div className="space-y-3">
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                      minLength={6}
                      autoComplete="new-password"
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Ingresa el nombre"
                    />
                    <button
                      type="button"
                      onClick={togglePasswordVisibility}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>

                  <button
                    type="button"
                    onClick={handleGeneratePassword}
                    className={estiloDeBoton('secundario', 'w-full')}
                    title="Generar contraseña segura"
                  >
                    <RefreshCw className="w-4 h-4" />
                    <span>Generar</span>
                  </button>
                </div>

                {/* Password Strength Indicator */}
                {formData.password && (
                  <div className="mt-4">
                    <div className="mb-3">
                      <p className="text-sm font-medium text-gray-700 mb-2">Fuerza de la contraseña</p>

                      {/* Progress Bar */}
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all duration-300 ${passwordStrength.color === 'green' ? 'bg-green-500' :
                            passwordStrength.color === 'yellow' ? 'bg-yellow-500' : 'bg-red-500'
                            }`}
                          style={{ width: `${passwordStrength.score}%` }}
                        ></div>
                      </div>
                    </div>

                    {/* Password Requirements Checklist */}
                    <div className="space-y-2 text-xs">
                      <div className={`flex items-center space-x-2 ${passwordStrength.checks.length ? 'text-green-600' : 'text-gray-400'}`}>
                        <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${passwordStrength.checks.length ? 'bg-green-100' : 'bg-gray-100'}`}>
                          {passwordStrength.checks.length ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                        </div>
                        <span>Al menos 8 caracteres</span>
                      </div>

                      <div className={`flex items-center space-x-2 ${passwordStrength.checks.lowercase ? 'text-green-600' : 'text-gray-400'}`}>
                        <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${passwordStrength.checks.lowercase ? 'bg-green-100' : 'bg-gray-100'}`}>
                          {passwordStrength.checks.lowercase ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                        </div>
                        <span>Letra minúscula</span>
                      </div>

                      <div className={`flex items-center space-x-2 ${passwordStrength.checks.symbols ? 'text-green-600' : 'text-red-600'}`}>
                        <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${passwordStrength.checks.symbols ? 'bg-green-100' : 'bg-gray-100'}`}>
                          {passwordStrength.checks.symbols ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                        </div>
                        <span>Carácter especial</span>
                      </div>

                      <div className={`flex items-center space-x-2 ${passwordStrength.checks.uppercase ? 'text-green-600' : 'text-red-600'}`}>
                        <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${passwordStrength.checks.uppercase ? 'bg-green-100' : 'bg-gray-100'}`}>
                          {passwordStrength.checks.uppercase ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                        </div>
                        <span>Letra mayúscula</span>
                      </div>

                      <div className={`flex items-center space-x-2 ${passwordStrength.checks.numbers ? 'text-green-600' : 'text-gray-400'}`}>
                        <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${passwordStrength.checks.numbers ? 'bg-green-100' : 'bg-gray-100'}`}>
                          {passwordStrength.checks.numbers ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                        </div>
                        <span>Número</span>
                      </div>
                    </div>

                    <p className="text-xs text-gray-500 mt-3">
                      Usa el generador para una contraseña segura o crea tu propia siguiendo los requisitos de arriba
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Form Actions Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-6">
            <div className="flex items-center justify-end space-x-4">
              <button
                type="button"
                onClick={handleCancel}
                className={estiloDeBoton('secundario')}
              >
                <X className="w-4 h-4" />
                <span>Cancelar</span>
              </button>
              {userHasPermission(
                "generales.usuarios.base.delete",
                user?.rbac?.effectivePermissions || [],
              ) && (
                <button
                  type="submit"
                  disabled={isCreating}
                  className={estiloDeBoton('primario')}
                >
                  {isCreating ? (
                    <div className="girando rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  <span>{isCreating ? "Creando..." : "Crear Usuario"}</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </form>

      {/* Info Card */}
      <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <div className="w-6 h-6 bg-[#1e3a8a] rounded-full flex items-center justify-center">
              <span className="text-white text-xs font-bold">i</span>
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-medium text-[#1e3a8a]">Información del nuevo usuario</h3>
            <div className="mt-2 text-sm text-[#1e3a8a]">
              <ul className="list-disc list-inside space-y-1">
                <li>Los nuevos usuarios se crearán con el rol "usuario" por defecto</li>
                <li>Los usuarios estarán activos por defecto</li>
                <li>Los usuarios pueden cambiar su contraseña después del primer inicio de sesión</li>
                <li>Los usuarios administradores pueden modificar roles y permisos más tarde</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CreateUser
